// gameEngine.js — Space Supremacy · Phase 2 HTML5 Canvas renderer.
// Hosts the RAF loop, the 2.5D isometric 5-square trek layout, avatar movement
// (click-to-move with lerp + arrival callback), the combat arena render, and the
// floating hit-number animation vectors. Given snapshots each frame; owns no
// game rules (those live in combatEngine/stateManager).

import { CORRIDOR_LENGTH, TileState, CORRIDOR_TILES } from "./stateManager";
import hudSkins from "./hudSkins";

const DPR = Math.max(1, Math.min(3, typeof window !== "undefined" ? window.devicePixelRatio : 1));

export class SpaceEngine {
  constructor(canvas, { onTileArrive } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d", { willReadFrequently: true });
    this.onTileArrive = onTileArrive || (() => {});
    this.mode = "trek"; // "trek" | "combat"
    this.trek = { corridor: [], avatarTile: 0, nancy: false };
    this.combat = null; // reference to combat snapshot {heroes, enemies}
    this.floats = [];
    this.fx = []; // transient combat VFX: projectile streaks, impact sparks, glow rings
    this.images = new Map(); // url -> HTMLImageElement (with .__ready)
    this.backdrop = null;    // trek backdrop url
    this.squad = [];         // [{cardId, url, tier, color}] in deploy order
    this.avatar = { x: 0, y: 0 }; // pixel position
    this.target = null; // { tile, x, y }
    this.arriveTile = null; // pending arrival to report
    this.w = 0; this.h = 0;
    this.raf = null;
    this.last = 0;
    this.t = 0;
    this._onPointer = this._onPointer.bind(this);
    this._loop = this._loop.bind(this);
  }

  mount() {
    this.resize();
    this.canvas.addEventListener("pointerdown", this._onPointer);
    // snap avatar onto its starting tile
    const p = this.tileCenter(this.trek.avatarTile);
    this.avatar = { x: p.x, y: p.y };
    this.last = performance.now();
    this.raf = requestAnimationFrame(this._loop);
  }

  unmount() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this._onPointer);
  }

  resize() {
    const r = this.canvas.getBoundingClientRect();
    this.w = r.width; this.h = r.height;
    this.canvas.width = Math.round(r.width * DPR);
    this.canvas.height = Math.round(r.height * DPR);
    this.ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }

  setMode(m) { this.mode = m; }
  setTrek(trek) {
    this.trek = { ...this.trek, ...trek };
    // keep avatar snapped if not mid-move
    if (!this.target) {
      const p = this.tileCenter(this.trek.avatarTile);
      this.avatar = { x: p.x, y: p.y };
    }
  }
  setCombat(combat) { this.combat = combat; this.fx = []; if (combat && combat.enemies) combat.enemies.forEach((e) => e.card && this._img(e.card)); }

  // ── Assets (Phase 7) ────────────────────────────────────────────────────
  static TIER_BORDER = { S: "#f5c542", A: "#c084fc", R: "#4aa3ff" };
  _img(url) {
    if (!url) return null;
    let im = this.images.get(url);
    if (!im) {
      im = new Image();
      im.__ready = false;
      im.onload = () => { im.__ready = true; };
      im.src = url;
      this.images.set(url, im);
    }
    return im.__ready ? im : null;
  }
  setBackdrop(url) { this.backdrop = url || null; if (url) this._img(url); }
  setSquad(list) { this.squad = list || []; list?.forEach((s) => this._img(s.url)); }
  _squadOf(cardId) { return this.squad.find((s) => s.cardId === cardId); }

  // circular-cropped portrait with tier border (ctx.clip, cover-fit)
  _drawPortrait(ctx, x, y, r, img, border) {
    ctx.save();
    ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.closePath(); ctx.clip();
    const iw = img.naturalWidth || 1, ih = img.naturalHeight || 1;
    const s = Math.max((2 * r) / iw, (2 * r) / ih);
    ctx.drawImage(img, x - (iw * s) / 2, y - (ih * s) / 2, iw * s, ih * s);
    ctx.restore();
    ctx.lineWidth = 3; ctx.strokeStyle = border || "rgba(255,255,255,0.6)";
    ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.stroke();
  }

  // ── Layout math ────────────────────────────────────────────────────────────
  tileCenter(i) {
    const cx = this.w * 0.5;
    const bottomY = this.h * 0.8;
    const topY = this.h * 0.26;
    const t = CORRIDOR_LENGTH > 1 ? i / (CORRIDOR_LENGTH - 1) : 0;
    const y = bottomY + (topY - bottomY) * t;
    const scale = 1 - 0.4 * t; // depth falloff
    return { x: cx, y, scale };
  }

  unitScreenPos(unit) {
    if (unit.side === "hero") {
      const heroes = (this.combat && this.combat.heroes) || [];
      const n = heroes.length || 1;
      const top = this.h * 0.34, bot = this.h * 0.82;
      const x = this.w * (n >= 4 ? 0.22 : 0.28);
      const y = n <= 1 ? this.h * 0.5 : top + (bot - top) * (unit.slot / (n - 1));
      return { x, y };
    }
    const n = unit.total || 1;
    const spread = Math.min(this.w * 0.7, n * 96);
    const startX = this.w * 0.5 - spread / 2 + spread / (n * 2);
    const x = n === 1 ? this.w * 0.5 : startX + unit.slot * (spread / n);
    const y = this.h * 0.24 + (unit.boss ? -6 : 0);
    return { x, y };
  }

  spawnFloat(x, y, text, color, big = false) {
    this.floats.push({ x, y, text, color, life: 1, vy: -0.045, big });
  }
  spawnFloatAtUnit(unit, text, color, big = false) {
    const p = this.unitScreenPos(unit);
    this.spawnFloat(p.x, p.y - (unit.boss ? 48 : 34), text, color, big);
  }

  // ── Skill VFX (Phase 7 Item 4) — canvas-native, lightweight ───────────────
  _burstAt(x, y, color, big = false) {
    const n = big ? 14 : 9;
    for (let i = 0; i < n; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = 0.05 + Math.random() * (big ? 0.16 : 0.11);
      this.fx.push({ type: "spark", x, y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, life: 1, color: color || "#ffb347" });
    }
    this.fx.push({ type: "ring", x, y, r: big ? 8 : 5, rMax: big ? 44 : 28, life: 1, color: color || "#ff7a4a", lw: 3 });
  }
  // projectile streak from caster → target; detonates into a burst on arrival
  spawnProjectile(from, to, color, crit = false) {
    const a = this.unitScreenPos(from), b = this.unitScreenPos(to);
    this.fx.push({
      type: "proj", x0: a.x, y0: a.y - 4, tx: b.x, ty: b.y - 4,
      color: color || "#ffd479", p: 0, dur: crit ? 190 : 150, crit,
    });
  }
  spawnHealGlow(unit) {
    const p = this.unitScreenPos(unit);
    this.fx.push({ type: "ring", x: p.x, y: p.y, r: 8, rMax: 40, life: 1, color: "#37e0a0", lw: 3 });
    for (let i = 0; i < 8; i++) {
      const ox = (Math.random() - 0.5) * 34;
      this.fx.push({ type: "spark", x: p.x + ox, y: p.y + 12, vx: (Math.random() - 0.5) * 0.02, vy: -0.05 - Math.random() * 0.06, life: 1, color: "#8effc9" });
    }
  }
  spawnAura(unit, color) {
    const p = this.unitScreenPos(unit);
    this.fx.push({ type: "ring", x: p.x, y: p.y, r: 6, rMax: 36, life: 1, color: color || "#00d5ff", lw: 2.5 });
  }
  // Full-canvas Ultimate strobe — a brief bright flash that decays over ~340ms.
  screenFlash(color = "#00e5ff") {
    this._flash = { color, t0: performance.now(), dur: 340 };
  }
  // Boss-spawn cinematic — "SQUAD ULTIMATES READY!" banner for ~1.5s (90 frames).
  ultBanner() {
    this._ultBanner = { t0: performance.now(), dur: 1500 };
  }
  // Valkyrie · Aegis Cleave — orange vertical slash wave across every enemy bound.
  slashWave(color = "#ff9a3c") {
    const foes = (this.combat && this.combat.enemies || []).filter((e) => e.alive);
    const targets = foes.length ? foes : [{ side: "enemy", slot: 0, total: 1 }];
    for (const f of targets) {
      const p = this.unitScreenPos(f);
      this.fx.push({ type: "slash", x: p.x, y: p.y, h: this.h * 0.34, color, life: 1 });
      for (let i = 0; i < 6; i++) this.fx.push({ type: "spark", x: p.x + (Math.random() - 0.5) * 30, y: p.y + (Math.random() - 0.5) * 40, vx: (Math.random() - 0.5) * 0.06, vy: -0.04 - Math.random() * 0.05, life: 1, color });
    }
  }
  // Roxy · Overdrive Cascade — neon-magenta field over the player's hero column.
  columnField(color = "#ff36c8") {
    const heroes = (this.combat && this.combat.heroes) || [];
    const n = heroes.length || 1;
    const x = this.w * (n >= 4 ? 0.22 : 0.28);
    this.fx.push({ type: "field", x: x - this.w * 0.14, y: this.h * 0.28, w: this.w * 0.28, h: this.h * 0.58, color, life: 1 });
    for (const hh of heroes.filter((u) => u.alive)) this.spawnAura(hh, color);
  }

  // ── Input ────────────────────────────────────────────────────────────────
  _onPointer(e) {
    if (this.mode !== "trek" || this.target) return;
    const r = this.canvas.getBoundingClientRect();
    const px = e.clientX - r.left, py = e.clientY - r.top;
    // find the objective tile = first UNCLEARED index
    const corridor = this.trek.corridor;
    let objective = corridor.findIndex((s) => s === TileState.UNCLEARED);
    if (objective < 0) return;
    // nearest tile to the tap
    let best = -1, bestD = 1e9;
    for (let i = 0; i < CORRIDOR_LENGTH; i++) {
      const c = this.tileCenter(i);
      const d = Math.hypot(c.x - px, c.y - py);
      if (d < bestD) { bestD = d; best = i; }
    }
    // only allow moving to the objective (or a cleared tile at/behind it)
    if (best === objective || (corridor[best] === TileState.CLEARED && best <= objective)) {
      const c = this.tileCenter(best);
      this.target = { tile: best, x: c.x, y: c.y };
      this.arriveTile = corridor[best] === TileState.UNCLEARED ? best : null;
    }
  }

  // ── Loop ────────────────────────────────────────────────────────────────
  _loop(now) {
    const dt = Math.min(48, now - this.last);
    this.last = now;
    this.t += dt;
    this._update(dt);
    this._render();
    this.raf = requestAnimationFrame(this._loop);
  }

  _update(dt) {
    // avatar movement lerp
    if (this.target) {
      this.avatar.x += (this.target.x - this.avatar.x) * 0.16;
      this.avatar.y += (this.target.y - this.avatar.y) * 0.16;
      if (Math.hypot(this.target.x - this.avatar.x, this.target.y - this.avatar.y) < 2) {
        this.avatar.x = this.target.x; this.avatar.y = this.target.y;
        this.trek.avatarTile = this.target.tile;
        const arrive = this.arriveTile;
        this.target = null; this.arriveTile = null;
        if (arrive !== null) this.onTileArrive(arrive);
      }
    }
    // age floats
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const f = this.floats[i];
      f.y += f.vy * dt;
      f.life -= dt / 1100;
      if (f.life <= 0) this.floats.splice(i, 1);
    }
    // advance combat VFX
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      if (f.type === "proj") {
        f.p += dt / f.dur;
        if (f.p >= 1) { this._burstAt(f.tx, f.ty, f.color, f.crit); this.fx.splice(i, 1); }
      } else if (f.type === "spark") {
        f.x += f.vx * dt; f.y += f.vy * dt; f.vy += 0.0005 * dt;
        f.life -= dt / 520; if (f.life <= 0) this.fx.splice(i, 1);
      } else if (f.type === "ring") {
        f.r += (f.rMax - f.r) * 0.18; f.life -= dt / 360;
        if (f.life <= 0) this.fx.splice(i, 1);
      } else if (f.type === "slash") {
        f.life -= dt / 320; if (f.life <= 0) this.fx.splice(i, 1);
      } else if (f.type === "field") {
        f.life -= dt / 900; if (f.life <= 0) this.fx.splice(i, 1);
      }
    }
  }

  _render() {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);
    if (this.mode === "trek") this._renderTrek(ctx);
    else {
      // Layer 1 — phase HUD skin as themed backdrop (opaque assets sit behind
      // the semi-transparent combat gradient); entities + DOM HUD draw on top.
      if (this.combat) {
        const boss = (this.combat.enemies || []).some((e) => e.boss);
        hudSkins.draw(ctx, this.w, this.h, boss ? "boss" : "survival");
      }
      this._renderCombat(ctx); this._renderFx(ctx);
    }
    this._renderFloats(ctx);
    this._renderFlash(ctx);
    this._renderUltBanner(ctx);
  }

  _renderUltBanner(ctx) {
    if (!this._ultBanner) return;
    const k = (performance.now() - this._ultBanner.t0) / this._ultBanner.dur;
    if (k >= 1) { this._ultBanner = null; return; }
    const w = this.w, h = this.h, cy = h * 0.44;
    const alpha = Math.min(1, k / 0.12, k > 0.85 ? (1 - k) / 0.15 : 1);
    const bh = h * 0.11;
    ctx.save();
    ctx.globalAlpha = Math.max(0, alpha);
    // neon-orange banner bar slashing fully across the portrait viewport
    const grad = ctx.createLinearGradient(0, cy - bh / 2, 0, cy + bh / 2);
    grad.addColorStop(0, "rgba(255,120,20,0)");
    grad.addColorStop(0.5, "rgba(255,88,8,0.88)");
    grad.addColorStop(1, "rgba(255,120,20,0)");
    ctx.fillStyle = grad; ctx.fillRect(0, cy - bh / 2, w, bh);
    ctx.strokeStyle = "#ffb347"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, cy - bh / 2); ctx.lineTo(w, cy - bh / 2); ctx.moveTo(0, cy + bh / 2); ctx.lineTo(w, cy + bh / 2); ctx.stroke();
    // flashing, bouncing high-impact text
    const bounce = 1 + 0.12 * Math.sin(k * Math.PI * 6);
    const flash = 0.7 + 0.3 * Math.sin(performance.now() / 70);
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.shadowBlur = 24 * flash; ctx.shadowColor = "#ff7a1a";
    ctx.fillStyle = `rgba(255,240,222,${0.85 + 0.15 * flash})`;
    ctx.font = `900 ${Math.round(Math.min(w * 0.062, 30) * bounce)}px 'Barlow Condensed', system-ui, sans-serif`;
    ctx.fillText("🔥 SQUAD ULTIMATES READY! 🔥", w / 2, cy);
    ctx.restore();
  }

  _renderFlash(ctx) {
    if (!this._flash) return;
    const k = (performance.now() - this._flash.t0) / this._flash.dur;
    if (k >= 1) { this._flash = null; return; }
    // double-pulse strobe: bright at the start, quick fade
    const a = (1 - k) * (0.55 + 0.45 * Math.abs(Math.sin(k * Math.PI * 3)));
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(0.9, a));
    ctx.fillStyle = this._flash.color;
    ctx.fillRect(0, 0, this.w, this.h);
    ctx.restore();
  }

  _diamond(ctx, x, y, halfW, halfH) {
    ctx.beginPath();
    ctx.moveTo(x, y - halfH);
    ctx.lineTo(x + halfW, y);
    ctx.lineTo(x, y + halfH);
    ctx.lineTo(x - halfW, y);
    ctx.closePath();
  }

  _renderTrek(ctx) {
    // atmospheric backdrop (crash_landing) — cover-fit, dimmed
    const bg = this._img(this.backdrop);
    if (bg) {
      const iw = bg.naturalWidth || 1, ih = bg.naturalHeight || 1;
      const s = Math.max(this.w / iw, this.h / ih);
      ctx.globalAlpha = 0.35;
      ctx.drawImage(bg, this.w / 2 - (iw * s) / 2, this.h / 2 - (ih * s) / 2, iw * s, ih * s);
      ctx.globalAlpha = 1;
      const grad = ctx.createLinearGradient(0, 0, 0, this.h);
      grad.addColorStop(0, "rgba(6,7,15,0.55)"); grad.addColorStop(1, "rgba(6,7,15,0.85)");
      ctx.fillStyle = grad; ctx.fillRect(0, 0, this.w, this.h);
    }
    // connecting path spine
    ctx.strokeStyle = "rgba(0,213,255,0.18)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < CORRIDOR_LENGTH; i++) {
      const c = this.tileCenter(i);
      if (i === 0) ctx.moveTo(c.x, c.y); else ctx.lineTo(c.x, c.y);
    }
    ctx.stroke();

    // tiles far→near so nearer ones overlap
    for (let i = CORRIDOR_LENGTH - 1; i >= 0; i--) {
      const c = this.tileCenter(i);
      const s = this.trek.corridor[i] ?? TileState.LOCKED;
      const hw = 74 * c.scale, hh = 36 * c.scale;
      const objective = this.trek.corridor.findIndex((v) => v === TileState.UNCLEARED);

      if (s === TileState.CLEARED) {
        this._diamond(ctx, c.x, c.y, hw, hh);
        ctx.fillStyle = "rgba(16,185,129,0.16)";
        ctx.fill();
        ctx.strokeStyle = "rgba(16,185,129,0.5)"; ctx.lineWidth = 1.5; ctx.stroke();
      } else if (s === TileState.LOCKED) {
        this._diamond(ctx, c.x, c.y, hw, hh);
        ctx.fillStyle = "rgba(10,14,26,0.9)"; ctx.fill();
        ctx.strokeStyle = "rgba(90,100,120,0.25)"; ctx.lineWidth = 1; ctx.stroke();
        // fog speckle
        ctx.fillStyle = "rgba(120,130,150,0.10)";
        for (let k = 0; k < 8; k++) {
          const rx = c.x + (Math.random() - 0.5) * hw * 1.4;
          const ry = c.y + (Math.random() - 0.5) * hh * 1.4;
          ctx.fillRect(rx, ry, 2, 2);
        }
      } else {
        // uncleared / active — glowing objective node
        const pulse = 0.5 + 0.5 * Math.sin(this.t / 350);
        this._diamond(ctx, c.x, c.y, hw, hh);
        ctx.fillStyle = "rgba(245,158,11,0.16)"; ctx.fill();
        ctx.strokeStyle = `rgba(245,158,11,${0.45 + 0.4 * pulse})`;
        ctx.lineWidth = 2; ctx.stroke();
        this._drawObstacle(ctx, c, CORRIDOR_TILES[i].kind);
        if (i === objective) {
          ctx.fillStyle = `rgba(255,207,122,${0.7 + 0.3 * pulse})`;
          ctx.font = "700 10px 'JetBrains Mono', monospace";
          ctx.textAlign = "center";
          ctx.fillText("▼ TAP", c.x, c.y - hh - 12 * c.scale);
        }
      }
      // tile number label
      ctx.fillStyle = s === TileState.LOCKED ? "rgba(120,130,150,0.4)" : "rgba(232,246,255,0.8)";
      ctx.font = `700 ${Math.round(11 * c.scale)}px 'JetBrains Mono', monospace`;
      ctx.textAlign = "center";
      ctx.fillText(String(i + 1), c.x, c.y + hh + 14 * c.scale);
    }

    // avatar — deployed squad headshots (fall back to figures if art not ready)
    const lead = this.squad[0], second = this.squad[1];
    const leadImg = lead && this._img(lead.url);
    if (leadImg) this._drawPortrait(ctx, this.avatar.x - (second ? 12 : 0), this.avatar.y - 6, 18, leadImg, SpaceEngine.TIER_BORDER[lead.tier]);
    else this._drawHeroFigure(ctx, this.avatar.x - (this.trek.nancy ? 12 : 0), this.avatar.y, lead?.color || "#4aa3ff", 1);
    if (second) {
      const secImg = this._img(second.url);
      if (secImg) this._drawPortrait(ctx, this.avatar.x + 18, this.avatar.y + 2, 15, secImg, SpaceEngine.TIER_BORDER[second.tier]);
      else this._drawHeroFigure(ctx, this.avatar.x + 16, this.avatar.y + 4, second?.color || "#37e0a0", 0.86);
    }
  }

  _drawObstacle(ctx, c, kind) {
    ctx.save();
    ctx.translate(c.x, c.y - 4);
    const sc = c.scale;
    if (kind === "scavenge") {
      ctx.fillStyle = "#c9762e";
      ctx.fillRect(-14 * sc, -8 * sc, 28 * sc, 14 * sc);
      ctx.fillStyle = "#ffcf7a";
      ctx.fillRect(-6 * sc, -14 * sc, 12 * sc, 8 * sc);
    } else if (kind === "rescue") {
      ctx.fillStyle = "#5a6b8c"; // shuttle plating
      ctx.fillRect(-18 * sc, -6 * sc, 36 * sc, 12 * sc);
      ctx.fillStyle = "#37e0a0"; // trapped medic glow
      ctx.beginPath(); ctx.arc(0, -2 * sc, 5 * sc, 0, 7); ctx.fill();
    } else if (kind === "boss") {
      ctx.fillStyle = "#33405e"; // sealed blast door
      ctx.fillRect(-20 * sc, -18 * sc, 40 * sc, 24 * sc);
      ctx.fillStyle = "#ff5a6a";
      ctx.beginPath(); ctx.arc(0, -6 * sc, 8 * sc, 0, 7); ctx.fill();
    } else {
      // combat beast
      ctx.fillStyle = "#ff5a6a";
      ctx.beginPath(); ctx.arc(0, -6 * sc, 9 * sc, 0, 7); ctx.fill();
      ctx.fillStyle = "#7a1020";
      ctx.fillRect(-3 * sc, -8 * sc, 2 * sc, 2 * sc);
      ctx.fillRect(2 * sc, -8 * sc, 2 * sc, 2 * sc);
    }
    ctx.restore();
  }

  _drawHeroFigure(ctx, x, y, color, scale) {
    ctx.save();
    ctx.translate(x, y);
    // shadow
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.beginPath(); ctx.ellipse(0, 10 * scale, 14 * scale, 5 * scale, 0, 0, 7); ctx.fill();
    // body
    ctx.fillStyle = color;
    ctx.fillRect(-8 * scale, -18 * scale, 16 * scale, 22 * scale);
    // head
    ctx.beginPath(); ctx.arc(0, -24 * scale, 6 * scale, 0, 7); ctx.fill();
    // rim light
    ctx.strokeStyle = "rgba(255,255,255,0.4)"; ctx.lineWidth = 1;
    ctx.strokeRect(-8 * scale, -18 * scale, 16 * scale, 22 * scale);
    ctx.restore();
  }

  _drawEnergyBar(ctx, x, y, width, height, currentEnergy, maxEnergy) {
    const fillPercent = Math.min(1, currentEnergy / maxEnergy);
    ctx.fillStyle = "rgba(0, 40, 60, 0.6)";
    ctx.fillRect(x, y, width, height);
    ctx.fillStyle = fillPercent === 1 ? "#5ff3ff" : "#00b8ff";
    ctx.fillRect(x, y, width * fillPercent, height);
    if (fillPercent === 1) {
      ctx.shadowBlur = 12;
      ctx.shadowColor = "#00e5ff";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x, y, width, height);
      ctx.shadowBlur = 0;
    }
  }

  _renderCombat(ctx) {
    if (!this.combat) return;
    // arena floor
    const g = ctx.createLinearGradient(0, 0, 0, this.h);
    g.addColorStop(0, "rgba(20,10,26,0.5)");
    g.addColorStop(1, "rgba(6,7,15,0.9)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, this.w, this.h);

    const all = [...this.combat.enemies, ...this.combat.heroes];
    for (const u of all) {
      if (!u.alive && u.side === "enemy") continue;
      const p = this.unitScreenPos(u);
      const r = u.boss ? 30 : u.side === "hero" ? 20 : 18;
      // shadow
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.beginPath(); ctx.ellipse(p.x, p.y + r * 0.9, r * 0.9, r * 0.4, 0, 0, 7); ctx.fill();
      // body — hero portrait (tier-framed) when art is ready, else colour disc
      const sq = u.side === "hero" ? this._squadOf(u.id) : null;
      let portrait = sq && u.alive ? this._img(sq.url) : null;
      let borderCol = sq ? SpaceEngine.TIER_BORDER[sq.tier] : null;
      if (!portrait && u.side === "enemy" && u.card && u.alive) {
        portrait = this._img(u.card);
        borderCol = u.boss ? "#ff2d55" : "#ff7a4a"; // heavy boss frame vs standard
      }
      if (portrait) {
        if (u.shield > 0) { // shield ring
          ctx.strokeStyle = "rgba(0,213,255,0.85)"; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.arc(p.x, p.y, r + 5, 0, 7); ctx.stroke();
        }
        this._drawPortrait(ctx, p.x, p.y, r, portrait, borderCol);
        if (u.boss) { ctx.strokeStyle = "rgba(255,45,85,0.6)"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, r + 6, 0, 7); ctx.stroke(); }
        if (u.taunt) { ctx.strokeStyle = "rgba(245,158,11,0.9)"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, r + 8, 0, 7); ctx.stroke(); }
      } else {
        ctx.fillStyle = u.alive ? u.color : "rgba(90,90,100,0.5)";
        ctx.globalAlpha = u.alive ? 1 : 0.5;
        ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, 7); ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = "rgba(255,255,255,0.35)"; ctx.lineWidth = 1.5; ctx.stroke();
      }
      // hp bar
      const bw = u.boss ? 120 : 62, bh = 6, bx = p.x - bw / 2, by = p.y - r - 16;
      ctx.fillStyle = "rgba(0,0,0,0.6)"; ctx.fillRect(bx - 1, by - 1, bw + 2, bh + 2);
      ctx.fillStyle = u.side === "hero" ? "#37e0a0" : "#ff5a6a";
      ctx.fillRect(bx, by, bw * (u.hp / u.maxHp), bh);
      // energy bar (heroes only) — Tier-4 Ultimate charge
      if (u.side === "hero") this._drawEnergyBar(ctx, bx, by + bh + 2, bw, 4, u.energy || 0, u.maxEnergy || 100);
      // name
      ctx.fillStyle = "rgba(232,246,255,0.85)";
      ctx.font = "700 10px 'JetBrains Mono', monospace";
      ctx.textAlign = "center";
      ctx.fillText(u.name, p.x, by - 5);
    }
  }

  _renderFx(ctx) {
    for (const f of this.fx) {
      if (f.type === "proj") {
        const x = f.x0 + (f.tx - f.x0) * f.p, y = f.y0 + (f.ty - f.y0) * f.p;
        let dx = f.tx - f.x0, dy = f.ty - f.y0;
        const len = Math.hypot(dx, dy) || 1; dx /= len; dy /= len;
        const tail = f.crit ? 26 : 18;
        const grad = ctx.createLinearGradient(x - dx * tail, y - dy * tail, x, y);
        grad.addColorStop(0, "rgba(255,255,255,0)");
        grad.addColorStop(1, f.color);
        ctx.strokeStyle = grad; ctx.lineWidth = f.crit ? 4 : 2.5; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(x - dx * tail, y - dy * tail); ctx.lineTo(x, y); ctx.stroke();
        ctx.fillStyle = f.color; ctx.beginPath(); ctx.arc(x, y, f.crit ? 4 : 3, 0, 7); ctx.fill();
      } else if (f.type === "spark") {
        ctx.globalAlpha = Math.max(0, f.life);
        ctx.fillStyle = f.color;
        ctx.beginPath(); ctx.arc(f.x, f.y, 2, 0, 7); ctx.fill();
        ctx.globalAlpha = 1;
      } else if (f.type === "ring") {
        ctx.globalAlpha = Math.max(0, f.life);
        ctx.strokeStyle = f.color; ctx.lineWidth = f.lw || 2;
        ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, 7); ctx.stroke();
        ctx.globalAlpha = 1;
      } else if (f.type === "slash") {
        // orange alpha-blended vertical slash streak
        const a = Math.max(0, f.life);
        const grad = ctx.createLinearGradient(f.x, f.y - f.h, f.x, f.y + f.h);
        grad.addColorStop(0, "rgba(255,154,60,0)");
        grad.addColorStop(0.5, f.color);
        grad.addColorStop(1, "rgba(255,154,60,0)");
        ctx.globalAlpha = a; ctx.strokeStyle = grad; ctx.lineWidth = 4 + 6 * a; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(f.x, f.y - f.h); ctx.lineTo(f.x + 8 * (1 - a), f.y + f.h); ctx.stroke();
        ctx.globalAlpha = 1;
      } else if (f.type === "field") {
        const a = Math.max(0, f.life) * (0.28 + 0.12 * Math.sin(performance.now() / 90));
        ctx.globalAlpha = a; ctx.fillStyle = f.color;
        ctx.beginPath(); ctx.roundRect(f.x, f.y, f.w, f.h, 16); ctx.fill();
        ctx.globalAlpha = Math.max(0, f.life); ctx.strokeStyle = f.color; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.roundRect(f.x, f.y, f.w, f.h, 16); ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }
  }

  _renderFloats(ctx) {
    ctx.textAlign = "center";
    for (const f of this.floats) {
      ctx.globalAlpha = Math.max(0, Math.min(1, f.life));
      ctx.fillStyle = f.color;
      ctx.font = `800 ${f.big ? 22 : 15}px 'Barlow Condensed', sans-serif`;
      ctx.fillText(f.text, f.x, f.y);
    }
    ctx.globalAlpha = 1;
  }
}
